import { useRef } from "react";
import { BezierCurve } from "../../bezier/bezier";
import { useBezierEditor } from "../../bezier/useBezierEditor";
import { useElementDimensions } from "../../hooks/useElementDimensions";
import { renderGrid, renderAxes, renderControlPoints, generateCurvePath, renderCurrentTimeIndicator, renderFilledArea } from "../../bezier/curveRenderer";

interface Props {
  controlPoints: BezierCurve;
  onControlPointsChange: (controlPoints: BezierCurve) => void;
  onHourHover?: (hour: number | null) => void;
  hoverHour?: number | null;
  width?: number;
  height?: number;
}

function SunCurve({
  controlPoints,
  onControlPointsChange,
  onHourHover,
  hoverHour,
  width,
  height,
}: Props) {
  // Use container-based dimensions if not specified
  const containerRef = useRef<HTMLDivElement>(null);
  const dimensions = useElementDimensions(
    containerRef,
    width,
    height,
    {
      defaultWidth: 600,
      defaultHeight: 200,
      widthOffset: 0,
      heightOffset: 0
    }
  );
  
  const actualWidth = dimensions.width;
  const actualHeight = dimensions.height;
  const margin = 40;
  const leftMargin = 60;
  const timeScale = 24;
  const solarScale = 200; // 0-200 MW solar capacity

  // Initialize bezier editor
  const {
    svgRef,
    dragging,
    timeToX,
    loadToY: solarToY,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleHover,
    handleMouseLeave,
  } = useBezierEditor({
    controlPoints,
    onControlPointsChange,
    width: actualWidth,
    height: actualHeight,
    margin: leftMargin,
    timeScale,
    loadScale: solarScale,
    onHover: onHourHover,
  });



  return (
    <div style={{ padding: "8px", background: "#f9f9f9", height: "100%", display: "flex", flexDirection: "column" }}>
      <h3 style={{ margin: "0 0 8px 0", fontSize: "14px", color: "#333" }}>
        Solar Availability Curve Editor
      </h3>

      <div ref={containerRef} style={{ flex: 1, position: "relative", minHeight: 0 }}>
        <svg
          ref={svgRef}
          width={actualWidth}
          height={actualHeight}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            border: "1px solid #ddd",
            background: "white",
            cursor: dragging ? "grabbing" : "default",
          }}
          onMouseMove={(e) => {
            handleMouseMove(e);
            if (!dragging) {
              handleHover(e);
            }
          }}
          onMouseUp={handleMouseUp}
          onMouseLeave={() => {
            handleMouseUp();
            handleMouseLeave();
          }}
      >
        {/* Grid */}
        {renderGrid({
          timeToX,
          valueToY: solarToY,
          actualWidth,
          actualHeight,
          margin,
          timeScale,
          valueScale: solarScale,
          leftMargin,
        })}

        {/* Axes */}
        {renderAxes({
          timeToX,
          valueToY: solarToY,
          actualWidth,
          actualHeight,
          margin,
          timeScale,
          valueScale: solarScale,
          valueLabel: "Solar Available (MW)",
          valueUnits: "",
          leftMargin,
        })}

        {/* Filled area under solar curve */}
        {renderFilledArea({
          controlPoints,
          timeToX,
          valueToY: solarToY,
          actualHeight,
          margin,
          fillColor: "#FFB347",
          fillOpacity: 0.2,
          timeScale,
          extendToTimeScale: true,
          leftMargin,
        })}

        {/* Solar curve */}
        <path
          d={generateCurvePath({
            controlPoints,
            timeToX,
            valueToY: solarToY,
            timeScale,
            extendToTimeScale: true,
          })}
          fill="none"
          stroke="#FFB347"
          strokeWidth={3}
        />

        {/* Control points */}
        {renderControlPoints({
          controlPoints,
          timeToX,
          valueToY: solarToY,
          handleMouseDown,
        })}

        {/* Hover hour indicator */}
        {renderCurrentTimeIndicator({
          hoverHour,
          timeToX,
          margin,
          actualHeight,
        })}
        </svg>
      </div>

    </div>
  );
}

export default SunCurve;